import React from 'react';

import type { LexicalNode } from '../types/lexical.types';

export function isLexicalTextNode(
  node: LexicalNode,
): node is LexicalNode & { type: 'text'; text: string } {
  return node.type === 'text' && typeof node.text === 'string';
}

export function extractTextFromNode(node: LexicalNode): string {
  if (!node) return '';

  if (node.type === 'linebreak') {
    return '\n';
  }

  if (isLexicalTextNode(node)) {
    return node.text;
  }

  if (Array.isArray(node.children)) {
    return node.children.map((child) => extractTextFromNode(child)).join('');
  }

  return '';
}

export function formatFromStyle(style: string | undefined): number {
  if (!style) return 0;
  let format = 0;
  if (/font-style\s*:\s*(italic|oblique)/i.test(style)) format |= 2;
  if (/font-weight\s*:\s*(bold|[7-9]00)/i.test(style)) format |= 1;
  if (/text-decoration[^;]*line-through/i.test(style)) format |= 4;
  if (/text-decoration[^;]*underline/i.test(style)) format |= 8;
  return format;
}

export function applyTextFormat(
  textNode: React.ReactNode,
  format: number,
  key: number,
): React.ReactNode {
  let result = textNode;

  if (format & 1) {
    result = <strong key={`bold-${key}`}>{result}</strong>;
  }
  if (format & 2) {
    result = <em key={`italic-${key}`}>{result}</em>;
  }
  if (format & 4) {
    result = <del key={`strike-${key}`}>{result}</del>;
  }
  if (format & 8) {
    result = <u key={`underline-${key}`}>{result}</u>;
  }
  if (format & 16) {
    result = (
      <code key={`code-${key}`} className="bg-gray-100 px-1 py-0.5 rounded text-sm">
        {result}
      </code>
    );
  }

  return result;
}

export function renderTextNode(
  node: LexicalNode & { text: string; style?: string },
  key: number,
  textColor: string = '#111827',
): React.ReactNode {
  if (node.type !== 'text' || typeof node.text !== 'string') {
    return null;
  }

  const text = node.text;
  const format = (node.format || 0) | formatFromStyle(node.style);

  if (!text.includes('\n')) {
    return (
      <span key={key} style={{ color: textColor }}>
        {applyTextFormat(text, format, key)}
      </span>
    );
  }

  const lines = text.split('\n');

  return (
    <span key={key} style={{ color: textColor }}>
      {lines.map((line: string, index: number) => (
        <React.Fragment key={`${key}-${index}`}>
          {index > 0 && <br />}
          {applyTextFormat(line, format, Number(`${key}${index}`))}
        </React.Fragment>
      ))}
    </span>
  );
}
